// src/steps/Step6PreviewNotes.js
import React, { useMemo, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Alert,
  Divider,
  Paper,
  Tooltip,
} from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearAllIcon from "@mui/icons-material/ClearAll";

import FrameworkHeader from "../components/FrameworkHeader";
import FrameworkCustomizer from "../components/FrameworkCustomizer";

// src/steps/Step6PreviewNotes.js
export default function Step6PreviewNotes({
  sessionId,
  preview,
  onRefreshPreview, // kept for backwards compatibility (not used by the reload button anymore)
  onClearPreview,

  futureEdu,
  setFutureEdu,
  futureGov,
  setFutureGov,
  futureTech,
  setFutureTech,
  futureEth,
  setFutureEth,

  alignmentNotes,
  setAlignmentNotes,
  notesByFramework,
  setNotesByFramework,

  shortlistPreview,

  certificationNeeded,

  // NEW: optional callback so App.js can enable/disable "Save Step 6" and "Next"
  onSelectionValidityChange,
}) {
  // Guard: if no session or preview, show simple controls
  const hasPreview = !!preview && !!preview.recap;

  // --- 1) AI reasons map (from Step 5 shortlistPreview) ---
  // Map of MLU id -> AI rationale text (from Step 5 shortlistPreview)
  const aiReasonsById = useMemo(() => {
    const map = {};
    (shortlistPreview || []).forEach((item) => {
      const id = item.id;
      if (!id) return;

      const reason =
        item.why ||
        item.reason ||
        item.rationale ||
        item.explanation ||
        item.why_selected ||
        item.whySelected;

      if (!reason) return;
      map[id] = reason;
    });
    return map;
  }, [shortlistPreview]);

  // --- 2) AI baseline selection (from shortlistPreview, NOT from recap.future) ---
  const aiSelection = useMemo(() => {
    const education = [];
    const governance = [];
    const technical = [];
    const ethics = [];

    (shortlistPreview || []).forEach((item) => {
      const id = item.id;
      if (!id || typeof id !== "string") return;

      if (id.startsWith("EDU-")) education.push(id);
      else if (id.startsWith("GOV-")) governance.push(id);
      else if (id.startsWith("TEC-")) technical.push(id);
      else if (id.startsWith("EID-")) ethics.push(id);
    });

    return {
      education,
      governance,
      technical,
      ethics,
    };
  }, [shortlistPreview]);

  const aiEdu = aiSelection.education || [];
  const aiGov = aiSelection.governance || [];
  const aiTech = aiSelection.technical || [];
  const aiEth = aiSelection.ethics || [];

  // --- 3) Notes per framework ---
  const frameworkNotes = notesByFramework || {};
  const eduNote = frameworkNotes.education || "";
  const govNote = frameworkNotes.governance || "";
  const techNote = frameworkNotes.technical || "";
  const ethNote = frameworkNotes.ethics || "";

  // When notesByFramework changes, keep a combined alignmentNotes string in sync.
  useEffect(() => {
    if (!setAlignmentNotes) return;

    const parts = [];

    if (eduNote.trim()) {
      parts.push(`EDU – Education Framework:\n${eduNote.trim()}`);
    }
    if (govNote.trim()) {
      parts.push(`GOV – Governance Framework:\n${govNote.trim()}`);
    }
    if (techNote.trim()) {
      parts.push(`TEC – Technical Design Framework:\n${techNote.trim()}`);
    }
    if (ethNote.trim()) {
      parts.push(`EID – Ethics & Inclusion Framework:\n${ethNote.trim()}`);
    }

    const combined = parts.join("\n\n");
    // Only update if different to avoid unnecessary re-renders
    if (combined !== (alignmentNotes || "")) {
      setAlignmentNotes(combined);
    }
  }, [eduNote, govNote, techNote, ethNote, setAlignmentNotes, alignmentNotes]);

  // Helper to update notesByFramework in a React-friendly way
  const updateFrameworkNote = (frameworkKey, value) => {
    if (!setNotesByFramework) return;
    setNotesByFramework((prev) => ({
      ...(prev || {}),
      [frameworkKey]: value,
    }));
  };

  // --- 4) Selection validity: at least one MLU selected across all frameworks ---
  const hasAnySelection =
    (futureEdu?.length || 0) +
    (futureGov?.length || 0) +
    (futureTech?.length || 0) +
    (futureEth?.length || 0) >
    0;

  // Notify parent (App.js) whenever the "hasAnySelection" changes,
  // so it can enable/disable "Save Step 6" and "Next".
  useEffect(() => {
    if (typeof onSelectionValidityChange === "function") {
      onSelectionValidityChange(hasAnySelection);
    }
  }, [hasAnySelection, onSelectionValidityChange]);

  // --- 5) Clear all selected MLUs in all frameworks (notes stay) ---
  const handleClearSelection = () => {
    setFutureEdu([]);
    setFutureGov([]);
    setFutureTech([]);
    setFutureEth([]);
    // hasAnySelection will become false → App.js can disable Save/Next via the effect above
  };

  // --- 6) Reload AI-suggested MLUs from local AI baseline (shortlistPreview) ---
  const handleReloadFromAiBaseline = () => {
    // Only do something if we actually have AI suggestions
    if (!aiEdu.length && !aiGov.length && !aiTech.length && !aiEth.length) {
      return;
    }

    setFutureEdu(aiEdu);
    setFutureGov(aiGov);
    setFutureTech(aiTech);
    setFutureEth(aiEth);
    // hasAnySelection will become true → App.js can enable Save/Next via the effect above

    // NOTE: we intentionally do NOT call onRefreshPreview here anymore.
    // The "reload" behaviour is now purely local: restore AI-suggested MLUs from shortlistPreview.
  };

  // Info text based on preview
  const recapTitle = useMemo(() => {
    if (!preview?.recap) return "T4R LJD Agent recap not loaded yet.";
    return "Step 6 \u00b7 Finalize and customize your future learning journey";
  }, [preview]);

  return (
    <Stack spacing={2}>
      {/* Top info / controls */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {recapTitle}
        </Typography>

        {!hasPreview && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No T4R LJD Agent recap is loaded yet for this session. Click{" "}
            <strong>"Reload T4R LJD Agent suggestions"</strong> to fetch the
            suggested future MLUs per framework.
          </Alert>
        )}

        {hasPreview && (
          <>
            {/* Instructions */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
                The MLUs suggested by the T4R LJD Agent are{" "}
                <strong>pre-selected below</strong>. You can{" "}
                <strong>add or remove MLUs for each framework</strong> and
                explain why you did it. You can see{" "}
                <em>"AI-generated selection only"</em> vs{" "}
                <em>"human"</em> and <em>"hybrid"</em> selections.
              </Typography>
            </Box>

            {/* Guidance note */}
            <Box sx={{ mb: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "rgba(0, 0, 0, 0.03)",
                  borderLeft: "4px solid",
                  borderColor: "grey.600",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <VerifiedUserOutlinedIcon
                  sx={{
                    fontSize: 24,
                    color: "grey.700",
                    mt: 0.2,
                  }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Please consider this only as a suggestion and{" "}
                    <strong>critically review</strong> whether the selection is
                    suitable for the learners from your perspective. Try to
                    enter notes based on your selection.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    These curated MLUs and notes will be used in{" "}
                    <strong>Step 7</strong> to build the final teaching plan.
                    If you reload the suggestions, the selection will be reset
                    to the <strong>AI-suggested MLUs</strong> but{" "}
                    <strong>your notes will still be preserved</strong>.
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </>
        )}

        {/* Actions – centred, equal styling */}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <Stack direction="row" spacing={1.5}>
            <Tooltip
              title={
                sessionId
                  ? "Reset the selection to the AI-suggested MLUs from Step 5. This keeps your notes unchanged."
                  : "Start or select a session before reloading suggestions."
              }
              arrow
            >
              <span>
                {/* span wrapper so Tooltip still works when button is disabled */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon fontSize="small" />}
                  onClick={handleReloadFromAiBaseline}
                  disabled={!sessionId}
                  sx={{ textTransform: "none", borderColor: "grey.400", color: "text.primary" }}
                >
                  Reload Agent suggestions
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title="Clear all selected MLUs in all frameworks. Notes stay as they are."
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearAllIcon fontSize="small" />}
                  onClick={handleClearSelection}
                  sx={{ textTransform: "none", borderColor: "grey.400", color: "text.primary" }}
                >
                  Clear selection
                </Button>
              </span>
            </Tooltip>

            {hasPreview && (
              <Tooltip
                title="Clear the current AI recap and go back to an empty state for Step 6."
                arrow
              >
                <Button
                  variant="text"
                  size="small"
                  onClick={onClearPreview}
                  color="secondary"
                  sx={{ textTransform: "none" }}
                >
                  Clear preview
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Box>
      </Box>

      <Divider />

      {/* If no preview, stop here */}
      {!hasPreview && (
        <Typography variant="body2" color="text.secondary">
          Once the recap is loaded, you will see the AI-suggested MLUs per
          framework here, with colour-coded borders indicating whether the final
          selection is AI-only, hybrid (AI + human), or human-only.
        </Typography>
      )}

      {hasPreview && (
        <Stack spacing={2}>
          {/*
            NOTE:
            For the sake of visual and conceptual consistency with Step 5,
            the framework titles + info tooltips are rendered via FrameworkHeader
            OUTSIDE the FrameworkCustomizer cards. This may be refactored later
            once the final UX pattern for framework sections is decided.
          */}

          {/* --- Education (EDU) --- */}
          <FrameworkHeader
            title="Education (EDU)"
            tooltip="Education (EDU) MLUs focus on foundational concepts, learning pathways, 
                     and capacity building for working with Local Digital Twins."
          />
          <FrameworkCustomizer
            codePrefix="EDU"
            mlus={[]} // catalog fetch handles metadata
            selected={futureEdu}
            aiSelection={aiEdu}
            aiReasons={aiReasonsById}
            imageSrc={`${process.env.PUBLIC_URL}/EDU.jpg`}
            notes={eduNote}
            certificationNeeded={certificationNeeded}
            onToggle={(id) =>
              setFutureEdu((prev) =>
                prev.includes(id)
                  ? prev.filter((x) => x !== id)
                  : [...prev, id]
              )
            }
            onNotesChange={(text) => updateFrameworkNote("education", text)}
          />

          {/* --- Ethics, Inclusion & Democratisation (EID) --- */}
          <FrameworkHeader
            title="Ethics, Inclusion & Democratisation (EID)"
            tooltip="EID MLUs address ethical use of data & AI, inclusive participation, 
                     and democratisation of Local Digital Twin practices."
          />
          <FrameworkCustomizer
            codePrefix="EID"
            mlus={[]}
            selected={futureEth}
            aiSelection={aiEth}
            aiReasons={aiReasonsById}
            imageSrc={`${process.env.PUBLIC_URL}/EID.jpg`}
            notes={ethNote}
            certificationNeeded={certificationNeeded}
            onToggle={(id) =>
              setFutureEth((prev) =>
                prev.includes(id)
                  ? prev.filter((x) => x !== id)
                  : [...prev, id]
              )
            }
            onNotesChange={(text) => updateFrameworkNote("ethics", text)}
          />

          {/* --- Governance (GOV) --- */}
          <FrameworkHeader
            title="Governance (GOV)"
            tooltip="Governance (GOV) MLUs address decision-making structures, coordination 
                     across departments, and the policy context of Local Digital Twins."
          />
          <FrameworkCustomizer
            codePrefix="GOV"
            mlus={[]}
            selected={futureGov}
            aiSelection={aiGov}
            aiReasons={aiReasonsById}
            imageSrc={`${process.env.PUBLIC_URL}/GOV.jpg`}
            notes={govNote}
            certificationNeeded={certificationNeeded}
            onToggle={(id) =>
              setFutureGov((prev) =>
                prev.includes(id)
                  ? prev.filter((x) => x !== id)
                  : [...prev, id]
              )
            }
            onNotesChange={(text) => updateFrameworkNote("governance", text)}
          />

          {/* --- Technical Design (TEC) --- */}
          <FrameworkHeader
            title="Technical Design (TEC)"
            tooltip="Technical Design (TEC) MLUs focus on data architectures, modelling, 
                     integration, and infrastructure for Local Digital Twins."
          />
          <FrameworkCustomizer
            codePrefix="TEC"
            mlus={[]}
            selected={futureTech}
            aiSelection={aiTech}
            aiReasons={aiReasonsById}
            imageSrc={`${process.env.PUBLIC_URL}/TEC.jpg`}
            notes={techNote}
            certificationNeeded={certificationNeeded}
            onToggle={(id) =>
              setFutureTech((prev) =>
                prev.includes(id)
                  ? prev.filter((x) => x !== id)
                  : [...prev, id]
              )
            }
            onNotesChange={(text) => updateFrameworkNote("technical", text)}
          />
        </Stack>
      )}
    </Stack>
  );
}
